import { ViewConfig } from "@lib/views";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    DATE_COLUMN_SIZE,
    AMOUNT_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { FieldReviewFieldValue } from "@common/types/transactionFieldReview";
import { HistoryDetailsBaseRow } from "../cells/historyDetailsBaseRow";
import { HistoryDetailsDateCell } from "../cells/HistoryDetailsDateCell";
import { HistoryDetailsAmountCell } from "../cells/HistoryDetailsAmountCell";
import { HistoryDetailsEntityCell } from "../cells/HistoryDetailsEntityCell";
import { FieldReviewBankDescriptionCell } from "./cells/FieldReviewBankDescriptionCell";
import { FieldReviewFieldCell } from "./cells/FieldReviewFieldCell";
import { FieldReviewValueCell } from "./cells/FieldReviewValueCell";
import { FieldReviewStatusCell } from "./cells/FieldReviewStatusCell";
import { FieldReviewFinancialAccountCell } from "./cells/FieldReviewFinancialAccountCell";
import { FieldReviewActionsCell } from "./cells/FieldReviewActionsCell";
import { FieldReviewReviewerCell } from "./cells/FieldReviewReviewerCell";

export interface FieldReviewDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    date: Date;
    logDate: Date;
    entityId: number | null;
    transactionId: number | undefined;
    bankDescription: string;
    reviewedField: string;
    fieldValue: FieldReviewFieldValue | FieldReviewFieldValue[] | null;
    reviewerIds: number[];
    isReviewedBefore: boolean;
    isReviewedAfter: boolean;
    financialAccountId: number | undefined;
    amount: number;
    resource?: Record<string, unknown>;
}

export interface FieldReviewTableCallbacks {
    onRowAction?: (row: FieldReviewDetailsRow) => void;
}

export type FieldReviewTableMeta = ViewTableMeta<
    FieldReviewTableCallbacks,
    unknown
>;

export enum FieldReviewDetailsColumn {
    DATE = "date",
    BANK_DESCRIPTION = "bankDescription",
    FIELD = "reviewedField",
    FIELD_VALUE = "fieldValue",
    REVIEWED_BY = "reviewedBy",
    PRIOR_STATUS = "isReviewedBefore",
    UPDATED_STATUS = "isReviewedAfter",
    FINANCIAL_ACCOUNT = "financialAccount",
    AMOUNT = "amount",
    ENTITY = "entity",
    ACTIONS = "actions",
}

export const fieldReviewDetailsViewConfig: ViewConfig<
    FieldReviewDetailsRow,
    FieldReviewDetailsColumn,
    never
> = {
    columns: {
        [FieldReviewDetailsColumn.DATE]: {
            name: "Date",
            header: "Date",
            accessorKey: "date",
            cell: HistoryDetailsDateCell,
            ...DATE_COLUMN_SIZE,
        },
        [FieldReviewDetailsColumn.BANK_DESCRIPTION]: {
            name: "Bank description",
            header: "Bank description",
            accessorKey: "bankDescription",
            cell: FieldReviewBankDescriptionCell,
            minSize: 200,
            maxSize: 500,
        },
        [FieldReviewDetailsColumn.FIELD]: {
            name: "Field",
            header: "Field",
            accessorKey: "reviewedField",
            cell: FieldReviewFieldCell,
            minSize: 120,
            maxSize: 200,
        },
        [FieldReviewDetailsColumn.FIELD_VALUE]: {
            name: "Value",
            header: "Value",
            accessorKey: "fieldValue",
            cell: FieldReviewValueCell,
            minSize: 160,
            maxSize: 300,
        },
        [FieldReviewDetailsColumn.REVIEWED_BY]: {
            name: "Reviewed by",
            header: "Reviewed by",
            accessorKey: "reviewerIds",
            cell: FieldReviewReviewerCell,
            minSize: 160,
            maxSize: 300,
        },
        [FieldReviewDetailsColumn.PRIOR_STATUS]: {
            name: "Prior status",
            header: "Prior status",
            accessorKey: "isReviewedBefore",
            cell: FieldReviewStatusCell,
            minSize: 130,
            maxSize: 200,
        },
        [FieldReviewDetailsColumn.UPDATED_STATUS]: {
            name: "Updated status",
            header: "Updated status",
            accessorKey: "isReviewedAfter",
            cell: FieldReviewStatusCell,
            minSize: 150,
            maxSize: 200,
        },
        [FieldReviewDetailsColumn.FINANCIAL_ACCOUNT]: {
            name: "Financial account",
            header: "Financial account",
            accessorKey: "financialAccountId",
            cell: FieldReviewFinancialAccountCell,
            minSize: 200,
            maxSize: 400,
        },
        [FieldReviewDetailsColumn.AMOUNT]: {
            name: "Amount",
            header: "Amount",
            accessorKey: "amount",
            cell: HistoryDetailsAmountCell,
            ...AMOUNT_COLUMN_SIZE,
        },
        [FieldReviewDetailsColumn.ENTITY]: {
            name: "Entity",
            header: "Entity",
            accessorKey: "entityId",
            cell: HistoryDetailsEntityCell,
            minSize: 56,
            maxSize: 150,
            immutable: true,
            hiddenFromSettings: true,
        },
        [FieldReviewDetailsColumn.ACTIONS]: {
            name: "Actions",
            header: "",
            accessorKey: "id",
            cell: FieldReviewActionsCell,
            minSize: 56,
            maxSize: 56,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
};
